import { useState, useEffect, useRef } from "react";

/**
 * useLastVisitedRow
 *
 * 用於「列表 → 明細 → 返回列表」情境。
 * 離開列表時呼叫 saveLastVisited 記錄 ID；
 * 返回列表時自動 highlight 對應列，並捲動到畫面中央。
 *
 * @param {object} options
 * @param {Array}  options.list             - 總覽資料列（用於判斷資料是否已載入）
 * @param {string} options.rowKey           - 資料列中比對用的欄位名稱（e.g. "cycleOrderID"）
 * @param {string} options.storageKey       - sessionStorage 使用的 key
 * @param {string} [options.highlightClass] - highlight 樣式的 CSS class，預設 "table-row-selected"
 *
 * @returns {{ saveLastVisited: (id: number|string) => void, rowClassName: (row: object) => string }}
 */
const useLastVisitedRow = ({ list, rowKey, storageKey, highlightClass = "table-row-selected" }) => {
	// 元件掛載時，從 sessionStorage 讀取上次進入的列 ID
	// lazy initializer 只執行一次，讀完立刻清除，避免頁面重整後仍殘留 highlight
	const [lastVisitedId] = useState(() => {
		const stored = sessionStorage.getItem(storageKey);
		sessionStorage.removeItem(storageKey);
		return stored ? Number(stored) : null; 	// sessionStorage 只存字串，轉回數字以利後續嚴格比對（===）
	});

	// 導向明細前呼叫此函式，將目標列 ID 暫存至 sessionStorage
	// 元件 unmount 後資料仍保留，返回時可正確取得
	const saveLastVisited = id => {
		sessionStorage.setItem(storageKey, id);
	};

	// 是否已處理過返回列表時的捲動；只在首次載入捲動一次，之後換頁／排序不再搶走捲動位置
	const hasScrolledRef = useRef(false);

	// 當列表資料首次載入完成後，將 highlight 列捲動到畫面中央
	useEffect(() => {
		if (hasScrolledRef.current || !lastVisitedId || !list?.length) return;

		// setTimeout 讓 React 完成 DOM 渲染後再查詢目標元素
		const timer = setTimeout(() => {
			hasScrolledRef.current = true;
			const el = document.querySelector(`.${highlightClass}`);
			if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 100);

		// cleanup：若 list 在 100ms 內再次更新，取消尚未執行的 timer
		return () => clearTimeout(timer);
	}, [list, lastVisitedId, highlightClass]);

	return {
		saveLastVisited, // 記錄當前列的 ID
		rowClassName: row => (lastVisitedId != null && row?.[rowKey] === lastVisitedId ? highlightClass : ""),
	};
};

export default useLastVisitedRow;
